import Image from "@Components/Image";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { useEffect, useRef } from "react";
import styles from "./AmberPlusOffers.module.scss";
import {
  CmsOffer,
  emitOfferClick,
  getOfferCategory,
  getPartnerLogo,
  getPartnerName,
} from "./offers";

type AmberPlusOfferCardsProps = {
  offers: CmsOffer[];
  showForDesktop?: boolean;
  /** Rail position of the first partner card, for `offer_index` analytics. */
  startIndex?: number;
};

/**
 * A bare fragment of Amber+ partner cards — no section chrome, no rail — so it can share one
 * HorizontalScroll with the promo cards in ReferralProgram.
 */
const OfferCards = ({
  offers,
  showForDesktop = false,
  startIndex = 1,
}: AmberPlusOfferCardsProps) => {
  const firstCardRef = useRef<HTMLAnchorElement>(null);
  const hasSentViewedEventRef = useRef(false);
  const platform = showForDesktop ? "web" : "mobile";

  useEffect(() => {
    if (!offers.length || hasSentViewedEventRef.current) {
      return undefined;
    }
    const el = firstCardRef.current;
    if (!el) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || hasSentViewedEventRef.current) return;
        hasSentViewedEventRef.current = true;
        gtagEventEmitter({
          action: "amber_plus_section_viewed",
          platform,
        });
        observer.disconnect();
      },
      { threshold: 0.2, rootMargin: "0px" },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [platform, offers.length]);

  if (!offers.length) return null;

  return (
    <>
      {offers.map((item: CmsOffer, index: number) => {
        const backgroundUrl = item.heroImage?.url || "";
        const title = item.offer?.title || "";
        const subtitle = item.offer?.subtitle || "";
        const redirectLink = item.offer?.redirectLink || "";
        const partnerName = getPartnerName(item.offer?.label);
        const offerCategory = getOfferCategory(item.offer?.label);
        const partnerLogo = getPartnerLogo(item);

        return (
          <a
            key={`${partnerName}-${index}`}
            ref={index === 0 ? firstCardRef : undefined}
            href={redirectLink}
            target="_blank"
            rel="noreferrer"
            className={styles["offer-card"]}
            onClick={() => emitOfferClick(partnerName, startIndex + index, offerCategory)}
          >
            <Image
              src={backgroundUrl}
              alt={item.heroImage?.alt || partnerName}
              width={showForDesktop ? 356 : 314}
              height={showForDesktop ? 162 : 170}
              className={styles["banner-image"]}
              isEagerLoad={index < 2}
            />
            <div className={styles.overlay} />
            <div className={styles["logo-pill"]}>
              {partnerLogo?.url ? (
                <Image
                  src={partnerLogo.url}
                  alt={partnerLogo.alt || partnerName}
                  width={showForDesktop ? 96 : 84}
                  height={showForDesktop ? 28 : 24}
                  className={styles["logo-image"]}
                  isEagerLoad={index < 2}
                />
              ) : (
                partnerName
              )}
            </div>
            <div className={styles.content}>
              <h3 className={styles.title}>{title}</h3>
              <p className={styles.subtitle}>{subtitle}</p>
            </div>
          </a>
        );
      })}
    </>
  );
};

export default wrapperHOC(OfferCards, {
  componentName: "AmberPlusOfferCards-Home",
  showForChina: true,
});
